// allow-same-origin is required so embedded apps can access localStorage, cookies, and
// IndexedDB on their own origin. Acceptable for first-party and vetted third-party apps.
export const IFRAME_SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads";

// Mirrors the size keys ui-kit's Drawer understands. elrond stores canvasSize as a free-form string
// with no enum, so anything can arrive from a manifest and has to be normalized before use.
export const CANVAS_SIZES = ["small", "medium", "large", "xl", "2xl"] as const;

export type CanvasSizeName = typeof CANVAS_SIZES[number];

const DEFAULT_CANVAS_SIZE: CanvasSizeName = "xl";

export const normalizeCanvasSize = (canvasSize?: string): CanvasSizeName =>
    CANVAS_SIZES.includes(canvasSize as CanvasSizeName) ? (canvasSize as CanvasSizeName) : DEFAULT_CANVAS_SIZE;

// small/medium/large/xl match ui-kit's Drawer widths (w-72/w-85/w-110/w-200) so a push drawer and an
// overlay drawer of the same size agree. ui-kit's 2xl is viewport-relative and lands at 768px until
// the 2xl breakpoint, which is too narrow for an app canvas, so 2xl is pinned to 1024 here.
export const CANVAS_SIZE_PX: Record<CanvasSizeName, number> = {
    small: 288,
    medium: 340,
    large: 440,
    xl: 800,
    "2xl": 1024,
};

// Clamped to the viewport so a wide canvas cannot push the dashboard off-screen on a small display.
// Not reactive to resize: the value is read when a canvas opens, which is when placement is decided.
export const canvasSizePx = (canvasSize?: string): number => {
    const width = CANVAS_SIZE_PX[normalizeCanvasSize(canvasSize)];
    return typeof window === "undefined" ? width : Math.min(width, window.innerWidth);
};
